import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getCurrencySymbol } from '@/utils/currency';
import { useVisibleScenarios } from '../../hooks/useVisibleScenarios';
import { usePermissions } from '../../hooks/usePermissions';
import { enrichScenarioViews, useScenarioViews } from '../../hooks/useScenarioViews';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTerminology } from '@/hooks/useTerminology';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { formatDate } from '../../lib/utils';
import { useFormatting } from '../../hooks/useFormatting';
import { useBaseForecast } from '../../hooks/useBaseForecast';
import { useScenarioForecasts } from '../../hooks/useScenarioForecasts';
import { useApp } from '../../contexts/AppContext';
import { applyScenarioToForecast } from '../../lib/scenarioEngine';
import { Search, X, Layers, TrendingUp, Scissors, AlertTriangle } from 'lucide-react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface ScenarioComparisonProps {
  /** If provided, this scenario will be pre-selected when the comparison view opens */
  initialScenarioId?: string;
}

export const ScenarioComparison: React.FC<ScenarioComparisonProps> = ({ initialScenarioId }) => {
  const { getCashDurationShortTerm, getCashDurationTerm, getScenarioTerm } = useTerminology();
  const scenarios = useVisibleScenarios();
  const { formatCurrency } = useFormatting();
  const { baseForecast } = useBaseForecast();
  const { mode } = useApp();
  const isLive = mode === 'live';
  const { hasPermission } = usePermissions();
  // Per engine spec §9.2, archived scenarios are only visible in the list to
  // the manager view — gated on the same permission as the Parked tab, matching
  // the gallery status filter.
  const canViewArchived = hasPermission('view_parked_scenarios');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isSelectorOpen, setIsSelectorOpen] = useState(true);
  const [drillDownPoint, setDrillDownPoint] = useState<{ date: string; values: { name: string; balance: number }[] } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Live mode: the selectable list is fetched from GraphQL (vw_Scenarios) with
  // the same combined search + category + status filter applied server-side.
  const liveView = useScenarioViews({
    search: searchQuery,
    category: categoryFilter,
    status: statusFilter,
  });

  const filteredScenarios = useMemo(() => {
    if (liveView.isLive) {
      // With a server-side filter active, the GraphQL view is authoritative for
      // membership — don't append context-only scenarios (they'd bypass it).
      const hasActiveServerFilter =
        searchQuery.trim() !== '' || categoryFilter !== 'all' || statusFilter !== 'all';
      return enrichScenarioViews(liveView.scenarios, scenarios, {
        appendContextOnly: !hasActiveServerFilter,
      });
    }
    return scenarios.filter(s => {
      const matchesSearch = !searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || s.category === categoryFilter;
      const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [scenarios, searchQuery, categoryFilter, statusFilter, liveView.isLive, liveView.scenarios]);

  const { data: scenarioForecastData } = useScenarioForecasts(selectedIds, '90d');

  // Pre-select only when navigating via a scenario card's Compare button
  useEffect(() => {
    if (initialScenarioId) {
      setSelectedIds([initialScenarioId]);
    }
  }, [initialScenarioId]);

  const toggleScenario = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(s => s !== id));
    } else if (selectedIds.length < 5) {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const selectedScenarios = scenarios.filter(s => selectedIds.includes(s.id));

  const categoryCounts = {
    all: scenarios.length,
    growth: scenarios.filter(s => s.category === 'growth').length,
    'cost-cutting': scenarios.filter(s => s.category === 'cost-cutting').length,
    crisis: scenarios.filter(s => s.category === 'crisis').length,
    custom: scenarios.filter(s => s.category === 'custom').length,
  };

  const statusCounts = {
    all: scenarios.length,
    draft: scenarios.filter(s => s.status === 'draft').length,
    'under-review': scenarios.filter(s => s.status === 'under-review').length,
    'pending-approval': scenarios.filter(s => s.status === 'pending-approval').length,
    active: scenarios.filter(s => s.status === 'active').length,
    committed: scenarios.filter(s => s.status === 'committed').length,
    parked: scenarios.filter(s => s.status === 'parked').length,
    archived: scenarios.filter(s => s.status === 'archived').length,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDotClick = (payload: any) => {
    const date = payload.date as string;
    const values = selectedScenarios.map(s => ({
      name: s.name,
      balance: (payload[s.id] as number) ?? 0,
    }));
    setDrillDownPoint({ date, values });
  };

  // Recharts v3: custom dot onClick doesn't fire and chart-level onClick
  // receives activeTooltipIndex as a *string* (e.g. "3"), not a number.
  // Track the hovered index via onMouseMove (always up-to-date) and read on click.
  const activeIdxRef = useRef<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChartMouseMove = (state: any) => {
    const raw = state?.activeTooltipIndex ?? state?.activeIndex;
    activeIdxRef.current = raw != null ? Number(raw) : null;
  };
  const handleChartClick = () => {
    const idx = activeIdxRef.current;
    if (idx != null && !Number.isNaN(idx) && chartData[idx]) {
      handleDotClick(chartData[idx]);
    }
  };

  // Generate chart data from scenario engine
  const scenarioColors = [
    'var(--accent-teal-500)',
    'var(--accent-warning-500)',
    'var(--accent-info-500)',
    'var(--accent-success-500)',
    'hsl(var(--chart-purple))',
  ];

  const chartData = useMemo(() => {
    if (baseForecast.length === 0) return [];

    if (isLive) {
      const availableIds = selectedScenarios.filter(s => scenarioForecastData[s.id]).map(s => s.id);
      const firstId = availableIds[0];
      if (!firstId) return [];
      const firstForecast = scenarioForecastData[firstId].projectedForecast;
      return firstForecast.map((point, idx) => {
        const dataPoint: Record<string, string | number> = { date: formatDate(new Date(point.date)) };
        for (const id of availableIds) {
          const forecast = scenarioForecastData[id];
          if (forecast.metrics.changeCount === 0) {
            dataPoint[id] = Math.round(baseForecast[idx]?.balance ?? 0);
          } else {
            dataPoint[id] = Math.round(forecast.projectedForecast[idx]?.scenarioBalance ?? 0);
          }
        }
        return dataPoint;
      });
    }

    const scenarioForecasts = new Map<string, { date: Date; balance: number }[]>();
    for (const scenario of selectedScenarios) {
      if (scenario.changes.length === 0) {
        scenarioForecasts.set(scenario.id, baseForecast.map(p => ({ date: p.date, balance: p.balance })));
      } else {
        const result = applyScenarioToForecast(baseForecast, scenario.changes);
        scenarioForecasts.set(scenario.id, result.projectedForecast.map(p => ({ date: p.date, balance: p.scenarioBalance })));
      }
    }

    return baseForecast.map((point: { date: Date; balance: number }, idx: number) => {
      const dataPoint: Record<string, string | number> = { date: formatDate(point.date) };
      for (const [id, forecast] of scenarioForecasts) {
        dataPoint[id] = forecast[idx]?.balance || 0;
      }
      return dataPoint;
    });
  }, [selectedScenarios, baseForecast, isLive, scenarioForecastData]);



  return (
    <div className="space-y-6">
      {/* Scenario Selection */}
      <Collapsible open={isSelectorOpen} onOpenChange={setIsSelectorOpen}>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle>Select {getScenarioTerm(true)} to Compare (up to 5)</CardTitle>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
              {isSelectorOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
        <CardContent className="space-y-4">
          {/* Filter bar — matches All Scenarios tab */}
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={`Search ${getScenarioTerm(false)}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
                {searchQuery && (
                  <button type="button" onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Category Pills */}
            <div className="flex items-center gap-2">
              {[
                { value: 'all', label: 'All', icon: <Layers className="h-3.5 w-3.5" />, count: categoryCounts.all },
                { value: 'growth', label: 'Growth', icon: <TrendingUp className="h-3.5 w-3.5" />, count: categoryCounts.growth },
                { value: 'cost-cutting', label: 'Cost-Cutting', icon: <Scissors className="h-3.5 w-3.5" />, count: categoryCounts['cost-cutting'] },
                { value: 'crisis', label: 'Crisis', icon: <AlertTriangle className="h-3.5 w-3.5" />, count: categoryCounts.crisis },
              ].map(({ value, label, icon, count }) => (
                <Button
                  key={value}
                  variant={categoryFilter === value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCategoryFilter(value)}
                  className="gap-1.5"
                >
                  {icon}
                  {label}
                  <Badge variant="secondary" className="ml-0.5">{count}</Badge>
                </Button>
              ))}
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status ({statusCounts.all})</SelectItem>
                <SelectItem value="draft">Draft {statusCounts.draft > 0 ? `(${statusCounts.draft})` : ''}</SelectItem>
                <SelectItem value="under-review">Under Review {statusCounts['under-review'] > 0 ? `(${statusCounts['under-review']})` : ''}</SelectItem>
                <SelectItem value="pending-approval">Pending Approval {statusCounts['pending-approval'] > 0 ? `(${statusCounts['pending-approval']})` : ''}</SelectItem>
                <SelectItem value="active">Active {statusCounts.active > 0 ? `(${statusCounts.active})` : ''}</SelectItem>
                <SelectItem value="committed">Committed {statusCounts.committed > 0 ? `(${statusCounts.committed})` : ''}</SelectItem>
                <SelectItem value="parked">Parked {statusCounts.parked > 0 ? `(${statusCounts.parked})` : ''}</SelectItem>
                {canViewArchived && (
                  <SelectItem value="archived">Archived {statusCounts.archived > 0 ? `(${statusCounts.archived})` : ''}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {(searchQuery || categoryFilter !== 'all' || statusFilter !== 'all') && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Showing <span className="font-semibold">{filteredScenarios.length}</span> of {scenarios.length} scenarios</span>
              <button type="button" onClick={() => { setSearchQuery(''); setCategoryFilter('all'); setStatusFilter('all'); }} className="text-primary hover:underline">Clear all filters</button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredScenarios.map((scenario) => {
              const isSelected = selectedIds.includes(scenario.id);
              return (
                <div
                  key={scenario.id}
                  className="flex items-start gap-3 p-3 border rounded-lg hover:bg-accent cursor-pointer"
                  onClick={() => toggleScenario(scenario.id)}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleScenario(scenario.id)}
                  />
                  <div className="flex-1">
                    <div className="font-semibold">{scenario.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {scenario.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
        </CollapsibleContent>
      </Card>
      </Collapsible>

      {selectedIds.length >= 2 && (
        <>
          {/* Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Cash Flow Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} onClick={handleChartClick} onMouseMove={handleChartMouseMove} style={{ cursor: 'pointer' }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      interval={Math.floor(90 / 6)}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `${getCurrencySymbol()}${(value / 1000).toFixed(0)}K`}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-popover text-popover-foreground border shadow-md rounded-md px-3 py-2 text-sm">
                              <p className="font-semibold mb-1.5">{payload[0]?.payload?.date}</p>
                              {payload.map((entry: any, index: number) => (
                                <div key={index} className="flex items-center gap-2">
                                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
                                  <span className="text-muted-foreground">{entry.name}:</span>
                                  <span className="font-semibold ml-auto pl-3">{formatCurrency(entry.value)}</span>
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    {selectedIds.map((id, i) => {
                      const scenario = scenarios.find(s => s.id === id);
                      return (
                        <Line
                          key={id}
                          type="monotone"
                          dataKey={id}
                          stroke={scenarioColors[i]}
                          strokeWidth={2}
                          name={scenario?.name || id}
                          dot={false}
                          activeDot={{ r: 5, cursor: 'pointer' }}
                        />
                      );
                    })}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              {/* Custom legend */}
              <div className="flex flex-wrap items-center justify-center gap-4 mt-3">
                {selectedIds.map((id, i) => {
                  const scenario = scenarios.find(s => s.id === id);
                  return (
                    <div key={id} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <div className="w-6 h-0.5" style={{ backgroundColor: scenarioColors[i] }} />
                      {scenario?.name || id}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Metrics Table */}
          <Card>
            <CardHeader>
              <CardTitle>Key Metrics Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Metric</TableHead>
                    {selectedScenarios.map((scenario) => (
                      <TableHead key={scenario.id} className="text-center">
                        {scenario.name}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Min Cash</TableCell>
                    {selectedScenarios.map((scenario) => (
                      <TableCell key={scenario.id} className="text-center">
                        <div className="font-semibold">{formatCurrency(scenario.metrics.minCash)}</div>
                        <div className="text-xs text-muted-foreground">{formatDate(scenario.metrics.minCashDate)}</div>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">{getCashDurationShortTerm().charAt(0).toUpperCase() + getCashDurationShortTerm().slice(1)}</TableCell>
                    {selectedScenarios.map((scenario) => (
                      <TableCell key={scenario.id} className="text-center font-semibold">
                        {getCashDurationTerm(scenario.metrics.runway)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Net Impact</TableCell>
                    {selectedScenarios.map((scenario) => (
                      <TableCell key={scenario.id} className="text-center">
                        <div className="font-semibold">
                          {scenario.metrics.netImpact >= 0 ? '+' : ''}
                          {formatCurrency(scenario.metrics.netImpact)}
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {selectedIds.length < 2 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Select at least 2 scenarios to compare
          </CardContent>
        </Card>
      )}

      {/* Drill-Down Dialog */}
      <Dialog open={drillDownPoint !== null} onOpenChange={(open) => { if (!open) setDrillDownPoint(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{drillDownPoint?.date} — Scenario Balances</DialogTitle>
          </DialogHeader>
          {drillDownPoint && (
            <div className="space-y-2 pt-2">
              <div className="rounded-lg border divide-y">
                {drillDownPoint.values.map((v) => (
                  <div key={v.name} className="flex items-center justify-between px-4 py-3">
                    <span className="text-sm text-muted-foreground">{v.name}</span>
                    <span className="text-sm font-semibold">{formatCurrency(v.balance)}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Projected balances at this date under each selected scenario.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
